import { useState, useEffect, useCallback } from 'react'
import { getApiUrl, fetchWithAuth } from '@/api'
import AdminPointAdjustPanel from './AdminPointAdjustPanel'

interface AdminPointHistoryItem {
  id: number
  member_id: number
  member_login_id: string 
  member_name: string
  type: number // 1=적립, 2=사용
  description: string | null
  amount: number
  balance_after: number
  ticket_no: string | null
  is_confirmed: number
  confirmed_at: string | null
  created_at: string
}

const LIMIT = 10

export default function AdminPointHistoryList() {
  const [histories, setHistories] = useState<AdminPointHistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [keyword, setKeyword] = useState('')
  const [keywordSearch, setKeywordSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [confirmingId, setConfirmingId] = useState<number | null>(null)

  const fetchHistories = useCallback(async (kw: string, type: string, p: number) => {
    setLoading(true)
    try {
        const params = new URLSearchParams()
        params.append('page', String(p))
        params.append('limit', String(LIMIT))
        if (type !== '') params.append('type', type)
        if (kw.trim()) params.append('keyword', kw.trim())

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/point/admin/histories?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setHistories(data.histories)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHistories(keywordSearch, typeFilter, page)
  }, [keywordSearch, typeFilter, page, fetchHistories])

  const handleSearch = () => {
    setKeywordSearch(keyword)
    setPage(1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleReset = () => {
    setKeyword('')
    setKeywordSearch('')
    setTypeFilter('')
    setPage(1)
  }
  const handleConfirm = async (id: number) => {
    if (!window.confirm('사용완료 처리하시겠습니까?')) return
    setConfirmingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/point/admin/histories/${id}/confirm`),
        { method: 'POST' }
      )
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail?.message ?? err.detail ?? '처리 실패')
      }
      fetchHistories(keywordSearch, typeFilter, page)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setConfirmingId(null)
    }
  }

  const handleReject = async (id: number, amount: number) => {
    if (!window.confirm(`사용을 취소하시겠습니까?\n${amount.toLocaleString()}P가 회원에게 반환되고 수량이 복구됩니다.`)) return
    setConfirmingId(id)
    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/point/admin/histories/${id}/reject`),
        { method: 'POST' }
      )
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.detail?.message ?? err.detail ?? '처리 실패')
      }
      fetchHistories(keywordSearch, typeFilter, page)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setConfirmingId(null)
    }
  }

  const formatDateTime = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const totalPages = Math.ceil(total / LIMIT)

  return (
    <>
        {/* Filter */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">이름 / 아이디</label>
              <input
                type="text"
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="이름 또는 아이디 검색"
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[180px] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">구분</label>
              <select
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[120px] bg-white"
              >
                <option value="">전체</option>
                <option value="1">적립</option>
                <option value="2">사용</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors h-[38px]"
              >검색</button>
              <button
                onClick={handleReset}
                className="bg-white text-[#887267] text-[13px] font-semibold px-4 py-2 rounded-lg border border-[#e8e0dc] cursor-pointer hover:bg-[#f5f2f0] transition-colors h-[38px]"
              >초기화</button>
            </div>
          </div>
          <div className="text-[13px] text-gray-400 font-medium">
            총 <span className="text-[#887267] font-bold">{total}</span>건
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[130px]">회원 아이디</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">이름</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[70px]">구분</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">내용</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">포인트</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">회원 총 포인트</th>
                  <th className="hidden lg:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[150px]">일시</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">사용확인</th>
                </tr>
              </thead>
              <tbody>
                {histories.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-300 text-[13px]">
                      내역이 없습니다.
                    </td>
                  </tr>
                ) : histories.map(h => (
                  <tr key={h.id} className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors">
                    <td className="px-4 py-3 text-[13px] text-[#2a1f1a] font-medium whitespace-nowrap">
                      {h.member_login_id}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{h.member_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                        h.type === 1
                          ? 'bg-[#e8f5e9] text-[#388e3c]'
                          : 'bg-[#fce4ec] text-[#c62828]'
                      }`}>
                        {h.type === 1 ? '적립' : '사용'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[13px] text-gray-600">
                      <div>{h.description ?? '-'}</div>
                      {h.ticket_no && (
                        <div className="text-[11px] text-gray-400 mt-0.5">티켓: {h.ticket_no}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-[13px] font-bold ${h.type === 1 ? 'text-[#388e3c]' : 'text-[#c62828]'}`}>
                        {h.type === 1 ? '+' : '-'}{h.amount.toLocaleString()}P
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-[13px] text-[#887267] font-semibold whitespace-nowrap">
                      {h.balance_after.toLocaleString()}P
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">
                      {formatDateTime(h.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {h.type === 2 ? (
                        h.is_confirmed === 2 ? (
                          <div>
                            <span className="text-[11px] px-2 py-0.5 rounded font-medium bg-[#f5f2f0] text-[#887267]">
                              취소됨
                            </span>
                            {h.confirmed_at && (
                              <div className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
                                {formatDateTime(h.confirmed_at)}
                              </div>
                            )}
                          </div>
                        ) : h.is_confirmed === 1 ? (
                          <div>
                            <span className="text-[11px] px-2 py-0.5 rounded font-medium bg-[#e3f2fd] text-[#1976d2]">
                              완료
                            </span>
                            {h.confirmed_at && (
                              <div className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
                                {formatDateTime(h.confirmed_at)}
                              </div>
                            )}
                            <div>
                              <button
                                onClick={() => handleReject(h.id, h.amount)}
                                disabled={confirmingId === h.id}
                                className="mt-1 text-[11px] text-gray-400 hover:text-[#c62828] underline bg-transparent border-none p-0 cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                              >
                                {confirmingId === h.id ? '처리중...' : '사용취소'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-start gap-1">
                            <button
                              onClick={() => handleConfirm(h.id)}
                              disabled={confirmingId === h.id}
                              className="text-[12px] text-[#887267] hover:text-[#6b5248] border border-[#e8d8ce] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              {confirmingId === h.id ? '처리중...' : '확인'}
                            </button>
                            <button
                              onClick={() => handleReject(h.id, h.amount)}
                              disabled={confirmingId === h.id}
                              className="text-[11px] text-gray-400 hover:text-[#c62828] underline bg-transparent border-none p-0 cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                              사용취소
                            </button>
                          </div>
                        )
                      ) : (
                        <span className="text-[12px] text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                  acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                  : <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                        page === p
                          ? 'bg-[#887267] text-white border-[#887267]'
                          : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                      }`}
                    >{p}</button>
              )}
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer"
            >›</button>
          </div>
        )}

        {/* 수동 증감 */}
        <AdminPointAdjustPanel
          onDone={() => {
            setPage(1)
            fetchHistories(keywordSearch, typeFilter, 1)
          }}
        />
    </>
  )
}